import { useState, useEffect, useCallback } from "react";
import {
  Box,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert
} from "@mui/material";
import axios from "axios";
import CustomTableWithPagination from "../../../components/customTable/CustomTable";
import {
  getFormattedDate,
  scrollToTop,
  getUserEmail
} from "../../../utils/commonFunctions";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadIcon from "../../../assets/icons/export.png";
import UserPointsDetailsPage from "./UserPointsDetailsPage";
import AddPointsDialog from "./components/AddPointsDialog";
import CustomButton from "../../../components/CustomButton/Button";

const apiEnpoint: any = process.env.REACT_APP_API_BASE_URL;

interface UserPointsTabProps {
  userId: string;
}

interface PointsData {
  PatronId: number;
  MerchantId: number;
  EstablishmentName: string;
  TotalPoints: string;
  LastPointCreated: string;
}

const UserPointsTab = ({ userId }: UserPointsTabProps) => {
  const [loading, setLoading] = useState(false);
  const [points, setPoints] = useState<{ data: PointsData[] } | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState<PointsData | null>(
    null
  );
  const [openAddPointsDialog, setOpenAddPointsDialog] = useState(false);
  const [businesses, setBusinesses] = useState<{ id: string; name: string }[]>(
    []
  );
  const [downloadingRowId, setDownloadingRowId] = useState<string | null>(null);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const downloadPointsHistory = async (row: PointsData) => {
    setMessage(null);
    if (!userId || !row?.MerchantId) return;

    const rowId = `${row.MerchantId}`;
    setDownloadingRowId(rowId);

    try {
      const url = `${apiEnpoint}/loyaltyprogram/points/user/${userId}/business/${row.MerchantId}/export`;

      await axios.post(
        url,
        {},
        {
          headers: {
            Authorization: `bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json"
          }
        }
      );

      // Get user email for the success message
      const userEmail = await getUserEmail();

      setMessage({
        type: "success",
        text: `Point history will be emailed to ${userEmail} shortly.`
      });
      scrollToTop();
    } catch (error: any) {
      console.error("Failed to export point history:", error);
      setMessage({
        type: "error",
        text:
          error.response?.data?.message ||
          "Failed to export point history. Please try again."
      });
      scrollToTop();
    } finally {
      setDownloadingRowId(null);
    }
  };

  const columns = [
    {
      id: "EstablishmentName",
      label: "Establishment Name",
      minWidth: 200,
      align: "left",
      format: (value: any) => value ?? "-"
    },
    {
      id: "TotalPoints",
      label: "Total Points",
      minWidth: 200,
      align: "left",
      format: (value: any) => value ?? "-"
    },
    {
      id: "LastPointCreated",
      label: "Last Point Created",
      minWidth: 200,
      align: "left",
      format: (value: any) => (value ? getFormattedDate(value) : "-")
    },
    {
      id: "MerchantId",
      label: "Merchant ID",
      minWidth: 200,
      align: "left",
      format: (value: any) => value ?? "-"
    },
    {
      id: "Export",
      label: "Export",
      minWidth: 100,
      align: "left",
      format: (_value: any, row: PointsData) => {
        const rowId = `${row.MerchantId}`;
        const isDownloading = downloadingRowId === rowId;
        return (
          <Tooltip title="Export point history">
            <IconButton
              size="small"
              disabled={isDownloading}
              onClick={(e) => {
                e.stopPropagation();
                downloadPointsHistory(row);
              }}
            >
              {isDownloading ? (
                <CircularProgress size={16} />
              ) : (
                <img
                  src={FileDownloadIcon}
                  alt="export file"
                  title="Export"
                  style={{ cursor: "pointer", width: "20px", height: "20px" }}
                />
              )}
            </IconButton>
          </Tooltip>
        );
      }
    }
  ];

  const fetchPoints = useCallback(() => {
    if (!userId) return;
    setLoading(true);
    axios
      .get(`${apiEnpoint}/loyaltyprogram/points/user/${userId}`, {
        headers: {
          Authorization: `bearer ${localStorage.getItem("accessToken")}`,
          "Content-Type": "application/json"
        }
      })
      .then((res) => setPoints(res.data))
      .catch(() => console.error("Failed to fetch points"))
      .finally(() => setLoading(false));
  }, [userId]);

  const fetchBusinesses = useCallback(() => {
    axios
      .get(`${apiEnpoint}/business`, {
        headers: {
          Authorization: `bearer ${localStorage.getItem("accessToken")}`,
          "Content-Type": "application/json"
        }
      })
      .then((res) => {
        setBusinesses(
          res.data.map((b: any) => ({
            id: b.Id || b.id,
            name: b.establishmentname || b.name
          }))
        );
      })
      .catch(() => setBusinesses([]));
  }, []);

  useEffect(() => {
    fetchPoints();
  }, [userId, fetchPoints]);

  useEffect(() => {
    if (openAddPointsDialog) fetchBusinesses();
  }, [openAddPointsDialog, fetchBusinesses]);

  const handleShowDetails = (merchant: PointsData) => {
    setSelectedMerchant(merchant);
    setDetailsDialogOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setSelectedMerchant(null);
  };

  return (
    <Box>
      {message && (
        <div style={{ marginBottom: "20px" }}>
          <Alert severity={message.type as any}>{message.text}</Alert>
        </div>
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 10,
            cursor: "pointer",
            alignItems: "center",
            justifyContent: "flex-end",
            width: "100%"
          }}
        >
          <CustomButton callBack={() => setOpenAddPointsDialog(true)}>
            Add Points
          </CustomButton>
          <RefreshIcon onClick={fetchPoints} />
        </div>
      </div>
      <CustomTableWithPagination
        columns={columns}
        data={points?.data || []}
        apiStatus={loading ? "fetching" : "idle"}
        payloadSize={100}
        currentPage={1}
        totalCount={points?.data?.length || 0}
        lastEvaluatedKey={null}
        handleChangeRowsPerPage={() => {}}
        handleChangePage={() => {}}
        isShowPagination={false}
        noDataFoundMessage="No data found."
        handleRowClick={handleShowDetails}
        sx={{ cursor: "pointer" }}
      />
      {selectedMerchant && (
        <UserPointsDetailsPage
          open={detailsDialogOpen}
          onClose={handleCloseDetails}
          userId={userId}
          businessId={selectedMerchant.MerchantId.toString()}
          establishmentName={selectedMerchant.EstablishmentName}
        />
      )}
      <AddPointsDialog
        open={openAddPointsDialog}
        onClose={() => {
          setOpenAddPointsDialog(false);
        }}
        userId={userId}
        businesses={businesses}
        onSuccess={() => {
          fetchPoints();
          setOpenAddPointsDialog(false);
        }}
      />
    </Box>
  );
};

export default UserPointsTab;
